import assign from 'lodash/assign';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isNil from 'lodash/isNil';

import type { FilterApiType } from '../types/ApiResponseTypes';
import type {
  activeTypesType,
  amenitiesType,
  bedroomsType,
  laundryType,
  petsType,
  propertyTypesType,
} from '../types/Filters';

export const defaultValues = {
  activeTypes: {
    active: true,
    inactive: true,
  },
  activatedDays: null,
  deactivatedDays: 30,
  propertyTypes: {
    apartment: true,
    condo: true,
    house: true,
    townhouse: true,
  },
  bedrooms: {
    '0': true,
    '1': true,
    '2': true,
    '3': true,
    '4plus': true,
  },
  pets: {
    cats: false,
    dogs: false,
  },
  laundry: {
    inUnit: false,
    shared: false,
  },
  amenities: {
    cooling: false,
    // dishwasher: false,
    // doorman: false,
    // fitnessCenter: false,
    // gatedEntry: false,
    heating: false,
    parking: false,
    // swimmingPool: false
  },
  orderBy: 'lowPrice',
  distanceInMiles: null,
  price: null,
};

export const clientServerConversion = {
  propertyTypes: {
    apartment: ['garden', 'large', 'medium'],
    condo: ['condo'],
    house: ['house'],
    townhouse: ['divided', 'townhouse'],
  },
};

export const serverToClientConversion = {
  propertyTypes: {
    large: 'apartment',
    garden: 'apartment',
    medium: 'apartment',
    divided: 'condo',
    condo: 'condo',
    house: 'house',
    townhouse: 'townhouse',
  },
};

const formatFilterObj = (_val: string | Record<string, unknown>, type: string) => {
  /**
   * possible val formats:
   * val = { active: true, inactive: true }
   * val = 'active,inactive'
   */
  // @ts-ignore
  const filterObj = assign({}, defaultValues[type]);

  if (!filterObj) {
    return null;
  }

  if (!_val) {
    return filterObj;
  }

  let newFilterObj = {};
  if (isString(_val)) {
    let val = _val;
    // @ts-ignore
    const conversion = serverToClientConversion[type];

    if (conversion) {
      // eslint-disable-next-line
      Object.entries(conversion).forEach((entry: [string, any]) => {
        val = val.replace(entry[0], entry[1]);
      });
    }

    newFilterObj = val
      .split(',')
      .sort()
      .filter((b) => Object.keys(filterObj).includes(b))
      .reduce((obj, el) => {
        // @ts-ignore
        obj[el] = true;
        return obj;
      }, {});
  } else if (isObject(_val)) {
    newFilterObj = _val;
  }

  return assign(filterObj, newFilterObj);
};

// handles creating filter from api and from partial filter objects on the client
class Filter {
  activeTypes: activeTypesType | null;
  activatedDays: number | null;
  deactivatedDays: number | null;
  propertyTypes: propertyTypesType | null;
  // listingTypes: listingTypesType | null;
  bedrooms: bedroomsType | null;
  pets: petsType | null;
  laundry: laundryType | null;
  amenities: amenitiesType | null;
  orderBy: string | null;
  distanceInMiles: number | null;
  price: number | null;
  limit: number | null;
  offset: number | null;

  constructor(res: FilterApiType = <FilterApiType>{}) {
    this.activeTypes = formatFilterObj(res.activeTypes, 'activeTypes');
    this.activatedDays = !isNil(res.activatedDays) ? res.activatedDays : defaultValues.activatedDays;
    this.deactivatedDays = !isNil(res.deactivatedDays) ? res.deactivatedDays : defaultValues.deactivatedDays;
    this.propertyTypes = formatFilterObj(res.propertyTypes, 'propertyTypes');
    // this.listingTypes = formatFilterObj(res.listingTypes, 'listingTypes');
    this.bedrooms = formatFilterObj(res.bedrooms, 'bedrooms');
    this.pets = formatFilterObj(res.pets, 'pets');
    this.laundry = formatFilterObj(res.laundry, 'laundry');
    this.amenities = formatFilterObj(res.amenities, 'amenities');

    this.deactivatedDays = !isNil(res.deactivatedDays) ? res.deactivatedDays : defaultValues.deactivatedDays;
    this.distanceInMiles = !isNil(res.distanceInMiles)
      ? Math.max(Math.min(Math.floor(res.distanceInMiles), 5), 1)
      : defaultValues.distanceInMiles;
    this.price = !isNil(res.price) ? res.price : null;

    // currently not customizable filters
    this.orderBy = 'lowPrice';
    this.limit = 20;
    this.offset = 0;
  }
}

export default Filter;
